/**
 * Custom Hook: useLocationsInfinite
 * Handles infinite scrolling pagination for locations using React Query.
 *
 * Uses getLocationsByFacilityPaginated (real API).
 * The Areas list must come only from this real endpoint: GET /api/v3/facilities/{facilityId}/locations?page=&limit=10.
 *
 * Features:
 * - Single source for the Areas list (real paginated API)
 * - Proper cache management
 * - Error handling
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getLocationsByFacilityPaginated } from '../services/locationsService';

/** Query key prefix for locations list. Use for refetch after create/update/delete/duplicate. */
export const LOCATIONS_QUERY_KEY = ['locations'];

/** Max items per page for locations (API and UX requirement). */
const LOCATIONS_LIMIT_PER_PAGE = 10;

/**
 * @param {Object} params
 * @param {string} params.facilityId
 * @param {string} [params.queryScope] - Isolates cache per context (e.g. 'organization-structure' vs 'cleaning-standards') so pagination in one step does not affect the other
 * @param {number} [params.limit=10] - Items per page (capped at 10)
 */
export const useLocationsInfinite = (params = {}) => {
  const { facilityId, queryScope = 'default', enabled = true, limit = 10, search = '' } = params;
  const limitPerPage = Math.min(Number(limit) || 10, LOCATIONS_LIMIT_PER_PAGE);
  const searchTerm = typeof search === 'string' ? search.trim() : '';
  const scope = typeof queryScope === 'string' && queryScope ? queryScope : 'default';

  const hasFacility = !!facilityId && String(facilityId).trim() !== '';

  const query = useInfiniteQuery({
    queryKey: [...LOCATIONS_QUERY_KEY, scope, facilityId, limitPerPage, searchTerm],
    queryFn: ({ pageParam, signal }) => {
      return getLocationsByFacilityPaginated(facilityId, {
        limit: limitPerPage,
        cursor: pageParam,
        search: searchTerm,
        signal,
      });
    },
    getNextPageParam: (lastPage) => {
      const { nextCursor } = lastPage?.pagination ?? {};
      return nextCursor ?? undefined;
    },
    initialPageParam: undefined,
    enabled: hasFacility && enabled,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Memoize flattened array so consumers get stable reference when data unchanged (avoids re-render loops)
  const locations = useMemo(
    () => query.data?.pages?.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const totalCount = query.data?.pages?.[query.data.pages.length - 1]?.pagination?.totalCount ?? 0;

  // hasNextPage from React Query
  const hasNextPage = query.hasNextPage ?? false;

  // Removed excessive logging to prevent infinite loops
  // Logs are only shown in development when explicitly needed for debugging

  return {
    locations,
    totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};
